<script setup lang="ts">
import { useToastStore, type ToastType } from '@/stores/toast';

const toastStore = useToastStore();

const getBorderColor = (type: ToastType): string => {
  switch (type) {
    case 'error':
      return 'border-red-200 bg-red-50/95 text-red-900 shadow-red-500/10';
    case 'success':
      return 'border-emerald-200 bg-emerald-50/95 text-emerald-900 shadow-emerald-500/10';
    case 'warning':
      return 'border-amber-200 bg-amber-50/95 text-amber-900 shadow-amber-500/10';
    case 'info':
    default:
      return 'border-blue-200 bg-blue-50/95 text-blue-900 shadow-blue-500/10';
  }
};

const getProgressBarColor = (type: ToastType): string => {
  switch (type) {
    case 'error':
      return 'bg-red-500';
    case 'success':
      return 'bg-emerald-500';
    case 'warning':
      return 'bg-amber-500';
    case 'info':
    default:
      return 'bg-blue-500';
  }
};
</script>

<template>
  <div
    class="pointer-events-none fixed top-4 right-4 z-[99999] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2.5 sm:top-6 sm:right-6 sm:w-96"
    aria-live="assertive"
  >
    <TransitionGroup name="toast">
      <div
        v-for="toast in toastStore.toasts"
        :key="toast.id"
        class="pointer-events-auto relative overflow-hidden rounded-xl border p-4 shadow-xl backdrop-blur-md transition-all duration-200"
        :class="getBorderColor(toast.type)"
        @mouseenter="toastStore.pause(toast.id)"
        @mouseleave="toastStore.resume(toast.id)"
      >
        <div class="flex items-start gap-3">
          <!-- Icon -->
          <div class="shrink-0 mt-0.5">
            <!-- Error Icon -->
            <svg
              v-if="toast.type === 'error'"
              class="h-5 w-5 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>

            <!-- Success Icon -->
            <svg
              v-else-if="toast.type === 'success'"
              class="h-5 w-5 text-emerald-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>

            <!-- Warning Icon -->
            <svg
              v-else-if="toast.type === 'warning'"
              class="h-5 w-5 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>

            <!-- Info Icon -->
            <svg
              v-else
              class="h-5 w-5 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0 pr-1">
            <h4 v-if="toast.title" class="text-sm font-semibold leading-5">
              {{ toast.title }}
            </h4>
            <p
              class="text-sm leading-relaxed"
              :class="toast.title ? 'mt-1 opacity-90' : 'font-medium'"
            >
              {{ toast.message }}
            </p>
          </div>

          <!-- Close Button -->
          <button
            type="button"
            class="shrink-0 rounded-lg p-1 text-ink-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ink-400"
            aria-label="Tutup notifikasi"
            @click="toastStore.remove(toast.id)"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Animated Progress Bar with Timer -->
        <div
          v-if="toast.duration > 0"
          class="absolute bottom-0 left-0 right-0 h-1 bg-black/5"
        >
          <div
            class="h-full toast-progress"
            :class="getProgressBarColor(toast.type)"
            :style="{
              animationDuration: `${toast.duration}ms`,
              animationPlayState: toast.pausedAt ? 'paused' : 'running',
            }"
          />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-enter-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  transition: all 0.25s ease-in;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(40px) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.92);
}

@keyframes toast-progress-shrink {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

.toast-progress {
  animation-name: toast-progress-shrink;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
</style>
